// File: src/api.js
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  collectionGroup,
  where,
  setDoc,
  writeBatch,
  increment,
  getFirestore,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * Create a test doc AND apply qstats increments for the supplied questions.
 *
 * questions: array of question objects (each must include chapter, assignment, qno)
 *
 * qstats doc id format: `${chapter}___${assignment}`
 * qstats doc shape: { "1": number, "2": number, ... }
 *
 * Minimizes writes by batching per-assignment increments in a single write batch.
 */
export async function createTestDoc({
  ownerUid,
  ownerEmail,
  title,
  isPublic,
  sharedWith = [],
  questions = [],
  timeLimitMins,
}) {
  // 1) write test doc (we will do it inside a batch together with qstats updates)
  const testsCol = collection(db, "tests");
  // We cannot get a docRef id via addDoc and also include it in the same writeBatch,
  // so create with addDoc first to get the id, then use a batch to update qstats.
  const docRef = await addDoc(testsCol, {
    ownerUid,
    ownerEmail,
    title,
    isPublic: !!isPublic,
    sharedWith: sharedWith || [],
    questions,
    timeLimitMins,
    createdAt: serverTimestamp(),
    results: {},
  });

  // 2) prepare qstats increments grouped by assignment doc
  // docId = `${chapter}___${assignment}`
  const incrementsByDoc = {}; // docId => { qnoStr: countToIncrement }
  for (const q of questions || []) {
    const chapter = q.chapter;
    const assignment = q.assignment;
    const qno = String(q.qno);
    const docId = `${chapter}___${assignment}`;
    if (!incrementsByDoc[docId]) incrementsByDoc[docId] = {};
    incrementsByDoc[docId][qno] = (incrementsByDoc[docId][qno] || 0) + 1;
  }

  // 3) build and commit a single batch: set/increment per qstat doc (merge) -- no reads needed
  const batch = writeBatch(db);
  for (const [docId, qmap] of Object.entries(incrementsByDoc)) {
    const qdocRef = doc(db, "qstats", docId);
    // convert to payload using increment
    const payload = {};
    for (const [qno, cnt] of Object.entries(qmap)) {
      payload[qno] = increment(cnt);
    }
    // use set with merge so doc is created if absent, and we can use increment fields
    batch.set(qdocRef, payload, { merge: true });
  }

  // commit qstats updates
  if (Object.keys(incrementsByDoc).length > 0) {
    await batch.commit();
  }

  return docRef.id;
}

/**
 * Fetch all tests (unchanged)
 */
export async function fetchAllTests() {
  const testsCol = collection(db, "tests");
  const snaps = await getDocs(query(testsCol, orderBy("createdAt", "desc")));
  return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchTestById(id) {
  const dref = doc(db, "tests", id);
  const snap = await getDoc(dref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// storeUserResult unchanged
export async function storeUserResult(testId, uid, resultData) {
  const tref = doc(db, "tests", testId);
  const snap = await getDoc(tref);
  if (!snap.exists()) throw new Error("Test not found");
  const data = snap.data();
  const newResults = { ...(data.results || {}) };
  const toStore = { ...resultData, submittedAt: serverTimestamp() };
  newResults[uid] = toStore;
  await updateDoc(tref, { results: newResults });
}

/**
 * Delete a test AND decrement qstats for its questions, ensuring counts don't go below 0.
 * - Reads the test doc once (to get its questions)
 * - Reads each affected qstats doc once (to know current counts)
 * - Writes the updated qstats (clamped) and deletes the test in one batch.
 *
 * This does more reads than the naive decrement-with-increment approach, but it ensures
 * qstats never become negative.
 */
export async function deleteTestById(testId) {
  if (!testId) return;

  const testRef = doc(db, "tests", testId);
  const testSnap = await getDoc(testRef);
  if (!testSnap.exists()) {
    // nothing to delete
    return;
  }
  const data = testSnap.data();
  const questions = Array.isArray(data.questions) ? data.questions : [];

  if (questions.length === 0) {
    // no questions: just delete the test doc
    await deleteDoc(testRef);
    return;
  }

  // group counts to decrement by qstat doc
  const decrementsByDoc = {}; // docId -> { qnoStr: count }
  for (const q of questions) {
    const chapter = q.chapter;
    const assignment = q.assignment;
    if (!chapter || !assignment || q.qno == null) continue;
    const qnoStr = String(q.qno);
    const docId = `${chapter}___${assignment}`;
    if (!decrementsByDoc[docId]) decrementsByDoc[docId] = {};
    decrementsByDoc[docId][qnoStr] = (decrementsByDoc[docId][qnoStr] || 0) + 1;
  }

  // read each qstats doc to compute new clamped values
  const docIds = Object.keys(decrementsByDoc);
  const qstatsCurrent = {}; // docId -> { existing qno -> number }
  await Promise.all(
    docIds.map(async (docId) => {
      const qdocRef = doc(db, "qstats", docId);
      const snap = await getDoc(qdocRef);
      qstatsCurrent[docId] = snap.exists() ? snap.data() : {};
    })
  );

  // prepare batch: for each qstat doc compute new values (clamped) and set them,
  // then delete the test doc.
  const batch = writeBatch(db);

  for (const docId of docIds) {
    const currentMap = qstatsCurrent[docId] || {};
    const decMap = decrementsByDoc[docId] || {};
    const newPayload = {};
    // For qnos mentioned in decMap: compute new = max(0, (current || 0) - dec)
    for (const [qno, decCount] of Object.entries(decMap)) {
      const existing = Number(currentMap[qno] || 0);
      const newVal = Math.max(0, existing - Number(decCount || 0));
      newPayload[qno] = newVal;
    }
    // IMPORTANT: We use set with merge to update only the modified qno fields.
    const qdocRef = doc(db, "qstats", docId);
    batch.set(qdocRef, newPayload, { merge: true });
  }

  // finally delete the test doc in same batch
  batch.delete(testRef);

  // commit
  await batch.commit();
}

export async function updateTestById(testId, updateData = {}) {
  const tref = doc(db, "tests", testId);
  await updateDoc(tref, updateData);
}

/**
 * Fetch qstats docs for a list of assignments (minimizes reads by fetching only requested docs).
 * input: array of { chapter, assignment } OR array of docIds (strings) - both supported
 * returns: { [docId]: { qno: frequency, ... } }
 */
export async function fetchQStatsForAssignments(items = []) {
  const docIds = items.map((it) =>
    typeof it === "string" ? it : `${it.chapter}___${it.assignment}`
  );
  // remove duplicates
  const uniqueIds = Array.from(new Set(docIds));
  const out = {};
  // fetch each doc (one read per distinct assignment)
  await Promise.all(
    uniqueIds.map(async (id) => {
      const qref = doc(db, "qstats", id);
      const snap = await getDoc(qref);
      if (snap.exists()) {
        out[id] = snap.data();
      } else {
        out[id] = {}; // treat missing doc as all-zero frequencies
      }
    })
  );
  return out;
}

/**
 * List bookmark groups for a user
 * Returns: [{ id, name, createdAt }]
 */
export async function fetchBookmarkGroups(uid) {
  if (!uid) return [];
  const groupsCol = collection(db, "users", uid, "bookmarkGroups");
  const q = query(groupsCol, orderBy("createdAt", "desc"));
  const snaps = await getDocs(q);
  return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createBookmarkGroup(uid, name) {
  const groupsCol = collection(db, "users", uid, "bookmarkGroups");
  const docRef = await addDoc(groupsCol, {
    name: name || "Bookmarks",
    createdAt: new Date(),
  });
  const snap = await getDoc(docRef);
  return { id: docRef.id, ...(snap.exists() ? snap.data() : {}) };
}

export async function getBookmark(uid, groupId, bookmarkId) {
  if (!uid || !groupId || !bookmarkId) return null;
  const bdoc = doc(
    db,
    "users",
    uid,
    "bookmarkGroups",
    groupId,
    "bookmarks",
    bookmarkId
  );
  const snap = await getDoc(bdoc);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function saveBookmark(uid, groupId, bookmarkId, data = {}) {
  if (!uid || !groupId || !bookmarkId) throw new Error("Missing identifiers");
  const bdoc = doc(
    db,
    "users",
    uid,
    "bookmarkGroups",
    groupId,
    "bookmarks",
    bookmarkId
  );
  const payload = {
    ...data,
    createdAt: new Date(),
  };
  await setDoc(bdoc, payload, { merge: true });
  const snap = await getDoc(bdoc);
  return { id: snap.id, ...snap.data() };
}

export async function deleteBookmark(uid, groupId, bookmarkId) {
  if (!uid || !groupId || !bookmarkId) throw new Error("Missing identifiers");
  const bdoc = doc(
    db,
    "users",
    uid,
    "bookmarkGroups",
    groupId,
    "bookmarks",
    bookmarkId
  );
  await deleteDoc(bdoc);
}

export async function fetchBookmarksForGroup(uid, groupId) {
  if (!uid || !groupId) return [];
  const bookmarksCol = collection(
    db,
    "users",
    uid,
    "bookmarkGroups",
    groupId,
    "bookmarks"
  );
  const snaps = await getDocs(bookmarksCol);
  return snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getBookmarkGroup(uid, groupId) {
  if (!uid || !groupId) return null;
  const bdoc = doc(db, "users", uid, "bookmarkGroups", groupId);
  const snap = await getDoc(bdoc);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}
